import { Button, Input } from "@ews-admin/global-design-system";
import { Select } from "../../components/atoms/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/molecules/Modal/Modal";
import {
  APP_NOTICE_ALL_VALUE,
  AppNoticeRuleFormData,
  appNoticeRuleSchema,
} from "../../schemas/appNotice.schema";
import {
  APP_NOTICE_COUNTRIES,
  APP_NOTICE_ENVIRONMENTS,
  AppNoticeDto,
  NoticeType,
  AppNoticeInput,
} from "../../types/appNotice.definition";

interface AppConfigRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: AppNoticeInput) => void;
  featureType: NoticeType;
  initial?: AppNoticeDto | null;
  isSaving: boolean;
  /**
   * Inline error to surface above the form actions (e.g. duplicate rule).
   * The parent owns the message and clears it when desired.
   */
  inlineError?: string | null;
}

// HTML <input type="datetime-local"> needs `YYYY-MM-DDTHH:mm` (no seconds, no
// timezone). The wire carries an absolute instant (`...T21:44:00Z`), so we
// convert it to the browser's local wall clock when pre-filling.
function toDatetimeLocal(value: string | null | undefined): string {
  if (!value) return "";
  // Cheap & robust: parse and rebuild to local datetime-local format.
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

// MED-1442: validFrom/validUntil are absolute instants on the wire, serialised
// with a trailing Z. The field holds the browser's local wall clock, so we send
// back the instant it denotes. The old +4h drift on every re-save came from the
// API storing that UTC instant as a naive wall clock and handing it back
// without a zone; now that the value round-trips with its Z, toISOString() here
// is the exact inverse of toDatetimeLocal() above and re-saving is stable.
function fromDatetimeLocal(value: string | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export const AppConfigRuleModal: React.FC<AppConfigRuleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  featureType,
  initial,
  isSaving,
  inlineError,
}) => {
  const { t } = useTranslation();
  const prevIsOpen = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<AppNoticeRuleFormData>({
    resolver: zodResolver(appNoticeRuleSchema(t)),
    mode: "onChange",
    defaultValues: {
      environment: APP_NOTICE_ALL_VALUE,
      country: APP_NOTICE_ALL_VALUE,
      enabled: true,
      validFrom: "",
      validUntil: "",
      messageEn: "",
      messageFr: "",
      messageAr: "",
      bgColor: "",
    },
  });

  // Reset form only when the modal opens (false -> true). Avoid resetting on
  // every render so the user's input survives re-renders.
  useEffect(() => {
    if (!isOpen || prevIsOpen.current === isOpen) {
      if (!isOpen) prevIsOpen.current = false;
      return;
    }
    prevIsOpen.current = isOpen;

    reset({
      environment: initial?.environment ?? APP_NOTICE_ALL_VALUE,
      country: initial?.country ?? APP_NOTICE_ALL_VALUE,
      enabled: initial?.enabled ?? true,
      validFrom: toDatetimeLocal(initial?.validFrom ?? null),
      validUntil: toDatetimeLocal(initial?.validUntil ?? null),
      messageEn: initial?.messageEn ?? "",
      messageFr: initial?.messageFr ?? "",
      messageAr: initial?.messageAr ?? "",
      bgColor: initial?.bgColor ?? "",
    });
  }, [isOpen, initial, reset]);

  const handleFormSubmit = (data: AppNoticeRuleFormData) => {
    const input: AppNoticeInput = {
      featureType,
      environment:
        data.environment === APP_NOTICE_ALL_VALUE
          ? null
          : (data.environment as AppNoticeInput["environment"]),
      country:
        data.country === APP_NOTICE_ALL_VALUE
          ? null
          : (data.country as AppNoticeInput["country"]),
      enabled: data.enabled,
      validFrom: fromDatetimeLocal(data.validFrom),
      validUntil: fromDatetimeLocal(data.validUntil),
      messageEn: data.messageEn || null,
      messageFr: data.messageFr || null,
      messageAr: data.messageAr || null,
      bgColor: data.bgColor || null,
    };
    onSubmit(input);
  };

  const envOptions = [
    { value: APP_NOTICE_ALL_VALUE, label: t("admin.governance.scope.all") },
    ...APP_NOTICE_ENVIRONMENTS.map(env => ({
      value: env,
      label: t(`admin.governance.environments.${env}`),
    })),
  ];

  const countryOptions = [
    { value: APP_NOTICE_ALL_VALUE, label: t("admin.governance.scope.all") },
    ...APP_NOTICE_COUNTRIES.map(c => ({
      value: c,
      label: t(`admin.governance.countries.${c}`),
    })),
  ];

  const enabled = watch("enabled");

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSaving && onClose()}
      title={
        initial
          ? t("admin.governance.announcement.editTitle")
          : t("admin.governance.announcement.createTitle")
      }
      size="md"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="p-6 space-y-4">
          {inlineError && (
            <div className="rounded-md bg-danger-50 border border-danger-200 p-3 text-sm text-danger-700">
              {inlineError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Controller
              name="environment"
              control={control}
              render={({ field }) => (
                <Select
                  label={t("admin.governance.form.environment")}
                  options={envOptions}
                  value={field.value}
                  onChange={value => field.onChange(value as string)}
                  error={errors.environment?.message}
                />
              )}
            />

            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <Select
                  label={t("admin.governance.form.country")}
                  options={countryOptions}
                  value={field.value}
                  onChange={value => field.onChange(value as string)}
                  error={errors.country?.message}
                />
              )}
            />
          </div>

          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={enabled}
                onChange={e => setValue("enabled", e.target.checked)}
                className="w-4 h-4 rounded border-neutral-300 text-ews-primary focus:ring-ews-primary"
              />
              <span className="text-sm font-medium text-neutral-700">
                {t("admin.governance.form.enabled")}
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t("admin.governance.form.validFrom")}
              type="datetime-local"
              fullWidth
              error={errors.validFrom?.message}
              {...register("validFrom")}
            />
            <Input
              label={t("admin.governance.form.validUntil")}
              type="datetime-local"
              fullWidth
              error={errors.validUntil?.message}
              {...register("validUntil")}
            />
          </div>

          {featureType === "ANNOUNCEMENT" && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-neutral-700">
                {t("admin.governance.form.messageSection")}
              </p>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("admin.governance.form.messageEn")}
                </label>
                <textarea
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ews-primary resize-none"
                  rows={3}
                  placeholder={t("admin.governance.form.messagePlaceholder")}
                  {...register("messageEn")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("admin.governance.form.messageFr")}
                </label>
                <textarea
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ews-primary resize-none"
                  rows={3}
                  placeholder={t("admin.governance.form.messagePlaceholder")}
                  {...register("messageFr")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("admin.governance.form.messageAr")}
                </label>
                {/* MED-1543: an Arabic-only field, so it reads
                    right-to-left whatever the UI language is. */}
                <textarea
                  dir="rtl"
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ews-primary resize-none"
                  rows={3}
                  placeholder={t("admin.governance.form.messagePlaceholder")}
                  {...register("messageAr")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("admin.governance.form.bgColor")}
                </label>
                <Controller
                  name="bgColor"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={field.value || "#000000"}
                        onChange={e => field.onChange(e.target.value)}
                        className="h-9 w-14 cursor-pointer rounded border border-neutral-300 p-1"
                      />
                      <Input
                        placeholder={t(
                          "admin.governance.form.bgColorPlaceholder"
                        )}
                        fullWidth
                        value={field.value || ""}
                        onChange={e => field.onChange(e.target.value)}
                        error={errors.bgColor?.message}
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end space-x-3">
          <Button
            variant="outline"
            type="button"
            onClick={onClose}
            disabled={isSaving}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            loading={isSaving}
            disabled={isSaving || !isValid}
          >
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
